import {Dict} from "wfr/commons/common";

/**
 * Interface de retour des résultats ES par le service Search.
 * size contient la taille uniquement si elle a été demandée par la requête et que le retour est exact (hits.total.relation == "eq")
 */
export interface JSearchResults<T>{
	size?:number
	results?:T[]
	agg:Dict<any>
}

/**
 * Interface de base d'un Hit
 */
export interface JHit{
	_index:string
	_id:string
	_score:number
}

/**
 * Hit avec sources. À n'utiliser que si nécessaire (privilégier les hits avec fields qui sont plus performants).
 */
export interface JHitSource<T> extends JHit{
	_source:T
}

/**
 * Hit avec fields. À privilégier, car plus performant que les hits avec source.
 */
export interface JHitFields<T> extends JHit{
	fields:T
}

export interface JDefaultFields{
	path:string[],
	processing:string[],
	title:string[]
}

/**
 * Service de recherche.
 */
export interface ISvcSearch<T>{
	search(code:string, props:any):Promise<JSearchResults<T>>
}

/**
 * Panel de recherche. Component graphique principal
 */
export interface ISearchPanel<T>{
	/**
	 * init du svc de recherche
	 */
	setSvc(svc:ISvcSearch<T>):void

	/**
	 * init du resultBuilder
	 */
	setResultBuilder(resultBuilder:ISearchResultBuilder):void

	/**
	 * lancement d'une recherche.
	 * @param forceProps
	 * Map de propriétés qui viennent écraser les propriétés compléter par les components.
	 *
	 * field : delete.field :"sort" -> supprime un field des propriétés
	 * field : reset.search : * -> n'exploite pas la map de components
	 */
	search(forceProps:Dict<any>):Promise<void>

	/**
	 * Lancement d"'une recherche dont le code sera `${request}More`
	 * @param from curseur à partir duquel demander les nouveaux résultats
	 */
	more(from:number):Promise<void>
}

/**
 * Mise en forme des résultats de recherche.
 */
export interface ISearchResultBuilder{
	/**
	 * Initialisation de l'élément Root du result Builder
	 */
	setRoot(element:HTMLElement):void

	/**
	 * Hook amont à la construction des résultats. Permet par exemple d'enrichir ou supprimer des résultats.
	 */
	resultsHook(results:JSearchResults<JHit>):JSearchResults<JHit>

	/**
	 * Élément HTML inséré avant la liste de résultats. Par exemple pour ajouter un titre avec le nombre de résultats obtenu
	 */
	beforeResults(results:JSearchResults<JHit>, total:number):HTMLElement

	/**
	 * Construit un élément de résultat à partir du hit.
	 */
	buildResult(result:JHit):HTMLElement

	/**
	 * Élément html inséré après la liste de résultat (et appeler après la construction des résultats).
	 * Permet d'ajouter des éléments de pagination.
	 */
	afterResults(results:JSearchResults<JHit>, total:number):HTMLElement

	/**
	 * Fonction appelée par la méthode more.
	 * Permet de mettre à jour le header avec par exemple le nombre de résultats
	 */
	beforeMoreResults(results:JSearchResults<JHit>, total:number):void
}

/**
 * Api d'un component d'affichage ou modification des props
 */
export abstract class SearchPropComponent extends HTMLElement{
	/**
	 * Prend la map de props en cours de construction et doit retourner une map de props complétée
	 */
	mergeProps(props:Dict<any>):Dict<any>{return props}

	/**
	 * Appelée après le retour de la requête search.
	 * Prend la map de props envoyée au moteur de recherche pour mettre à jour la construction graphique du component.
	 */
	renderProps(props:Dict<any>):void{}
}
